import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { Camera, X, Loader2, CheckCircle2, AlertTriangle, Upload } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_PHOTOS = 6;


export const ScanPublic = () => {
  const { token } = useParams();
  const [header, setHeader] = useState(null);
  const [resolveError, setResolveError] = useState('');
  const [photos, setPhotos] = useState([]);         // [{file, dataUrl}]
  const [status, setStatus] = useState('idle');     // idle | uploading | done | error
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/public/scan/${token}`)
      .then((r) => setHeader(r.data))
      .catch((e) => setResolveError(e?.response?.data?.detail || 'Scan link not found'));
  }, [token]);

  const addPhoto = async (ev) => {
    const files = Array.from(ev.target.files || []);
    for (const f of files) {
      if (photos.length + 1 > MAX_PHOTOS) break;
      const dataUrl = await new Promise((res) => {
        const reader = new FileReader();
        reader.onload = () => res(reader.result);
        reader.readAsDataURL(f);
      });
      setPhotos((p) => p.length < MAX_PHOTOS ? [...p, { file: f, dataUrl }] : p);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removePhoto = (idx) => setPhotos((p) => p.filter((_, i) => i !== idx));

  const submit = async () => {
    if (photos.length === 0) return;
    setStatus('uploading');
    const fd = new FormData();
    photos.forEach((p) => fd.append('images', p.file, p.file.name || 'scan.jpg'));
    try {
      const r = await axios.post(`${API}/public/scan/${token}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      setResult(r.data); setStatus('done');
    } catch (e) {
      setResult({ summary: e?.response?.data?.detail || 'Upload failed, please try again.' });
      setStatus('error');
    }
  };

  if (resolveError) return (
    <div className="min-h-screen bg-[#F4F0E6] flex items-center justify-center px-6">
      <div className="bg-white rounded-md shadow-sm p-6 max-w-sm w-full text-center">
        <AlertTriangle className="h-8 w-8 text-[#B06A2C] mx-auto mb-2" />
        <h2 className="font-semibold text-[#211C16] mb-1" style={{ fontFamily: 'Fraunces, serif' }}>Link unavailable</h2>
        <p className="text-sm text-text-muted">{resolveError}</p>
        <p className="text-xs text-text-muted mt-3">Ask the office to send a fresh link.</p>
      </div>
    </div>
  );

  if (!header) return (
    <div className="min-h-screen bg-[#F4F0E6] flex items-center justify-center">
      <Loader2 className="h-6 w-6 animate-spin text-[#6E5334]" />
    </div>
  );

  if (status === 'done') {
    const r = result || {};
    const flagged = (r.pending || 0) > 0;
    return (
      <div className="min-h-screen bg-[#F4F0E6] px-6 py-10" data-testid="scan-confirmation">
        <div className="max-w-sm mx-auto bg-white rounded-md shadow-sm p-6 text-center">
          <CheckCircle2 className="h-10 w-10 text-[#2F5D48] mx-auto mb-3" />
          <h2 className="text-xl font-semibold text-[#211C16] mb-2" style={{ fontFamily: 'Fraunces, serif' }}>
            Submitted
          </h2>
          <p className="text-sm text-[#211C16] mb-1">
            {r.summary || `${r.total_rows || 0} rows captured.`}
          </p>
          <p className="text-xs text-text-muted mt-2">See you next shift.</p>
          {flagged && (
            <p className="mt-4 text-[11px] text-[#B06A2C] bg-[#FBF4E8] border border-[#EDE5D3] rounded px-3 py-2">
              Some rows are being reviewed in the office before they land in the dashboard.
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F0E6] px-5 py-8" data-testid="scan-public-page">
      <div className="max-w-sm mx-auto">
        <div className="text-[11px] tracking-[.14em] uppercase text-[#6E5334] mb-1">Shift close · scan</div>
        <h1 className="text-2xl font-semibold text-[#211C16]" style={{ fontFamily: 'Fraunces, serif' }}>
          {header.client_name}
        </h1>
        <p className="text-sm text-text-secondary mt-1">
          Take photos of your Z-report and your card-machine settlement slip. Up to {MAX_PHOTOS} photos per submission.
        </p>

        <div className="mt-6 space-y-2">
          <label className="block">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              onChange={addPhoto}
              className="hidden"
              data-testid="scan-camera-input"
            />
            <div
              onClick={() => fileInputRef.current?.click()}
              className={`cursor-pointer rounded-md border-2 border-dashed border-[#B06A2C] bg-white px-5 py-6 text-center transition hover:bg-[#FBF8F0] ${photos.length >= MAX_PHOTOS ? 'opacity-50 pointer-events-none' : ''}`}
              data-testid="scan-camera-btn"
            >
              <Camera className="h-7 w-7 text-[#B06A2C] mx-auto mb-1.5" />
              <div className="font-semibold text-[#211C16]">
                {photos.length === 0 ? 'Scan Z-report' : 'Add another photo'}
              </div>
              <div className="text-[11px] text-text-muted mt-0.5">
                {photos.length} of {MAX_PHOTOS} captured
              </div>
            </div>
          </label>
        </div>

        {photos.length > 0 && (
          <div className="mt-4 grid grid-cols-3 gap-2" data-testid="scan-photo-strip">
            {photos.map((p, i) => (
              <div key={i} className="relative aspect-square rounded border border-[#EDE5D3] overflow-hidden bg-black" data-testid={`scan-photo-${i}`}>
                <img src={p.dataUrl} alt={`shot ${i+1}`} className="w-full h-full object-cover" />
                <button
                  onClick={() => removePhoto(i)}
                  className="absolute top-1 right-1 bg-black/60 rounded-full p-0.5 hover:bg-black/80"
                  data-testid={`scan-remove-${i}`}
                >
                  <X className="h-3.5 w-3.5 text-white" />
                </button>
                <div className="absolute bottom-1 left-1 bg-black/60 text-white text-[10px] px-1 rounded">
                  {i + 1}/{photos.length}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-6">
          <button
            onClick={submit}
            disabled={photos.length === 0 || status === 'uploading'}
            className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-[#2F5D48] text-white font-semibold py-3 disabled:bg-[#C4BBA2] disabled:cursor-not-allowed"
            data-testid="scan-submit-btn"
          >
            {status === 'uploading' ? (
              <><Loader2 className="h-4 w-4 animate-spin" /> Reading receipts…</>
            ) : (
              <><Upload className="h-4 w-4" /> Submit {photos.length > 0 ? `(${photos.length} photo${photos.length === 1 ? '' : 's'})` : ''}</>
            )}
          </button>
          {photos.length === 0 && (
            <div className="text-center text-[11px] text-text-muted mt-2">Add a photo first.</div>
          )}
        </div>

        {status === 'error' && result && (
          <div className="mt-4 text-xs text-state-error bg-red-50 border border-red-200 rounded p-2">
            {result.summary}
          </div>
        )}
      </div>
    </div>
  );
};

export default ScanPublic;
